import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Checkbox } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import Autocomplete from '@mui/material/Autocomplete';
import TextFieldFormik from 'src/components/TextFieldFormik';

import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import helpers from 'src/helpers';

import autocompleteFieldFormikStyle from 'src/components/AutocompleteFieldFormik/autocompleteFieldFormik.style';

function AutocompleteFieldFormik({
  classes,
  setFieldValue,
  showInfo,
  isShowInfoV2,
  touchedValue,
  errorValue,
  handleChange,
  handleBlur,
  disabledBox,
  values,
  filterOptions,
  name,
  removeNameFromRenderInput,
  label,
  disabled,
  options,
  inlineTextValidation,
  handleFocus,
  multiple,
  disableCloseOnSelect,
  renderOption,
  addHighlights,
  required,
  inputValue = undefined,
  onInputChange = undefined,
  maintainInputOnSelection = false, // Search persistence
  ...props
}) {
  const [fieldStateError, setFieldStateError] = useState(false);
  const [internalInputValue, setInternalInputValue] = useState('');
  const inputRef = useRef(null);
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const optionList = helpers.transform.arrayStringToObjectLabelValue(options);

  const focusStart = (event) => {
    if (handleFocus) {
      const newEvent = {
        ...event,
        dataName: name,
      };
      handleFocus(newEvent);
    }
  };

  const validateField = (value) => {
    if (!inlineTextValidation) {
      return;
    }
    Yup.reach(inlineTextValidation, name).validate(helpers.transform.toArray(value))
      .then(() => {
        setFieldStateError(false);
      })
      .catch((e) => {
        setFieldStateError(helpers.transform.toArray(e?.errors)[0]);
      });
  };

  const handleChangeAutocomplete = (event, value) => {
    setFieldValue(name, value);
    validateField(value);

    // NEW: For search persistence, restore the input value after selection
    if (maintainInputOnSelection && multiple && inputRef.current) {
      const currentSearchValue = inputValue !== undefined ? inputValue : internalInputValue;
      // Use setTimeout to restore the value after Material-UI's internal clearing
      setTimeout(() => {
        if (inputRef.current && currentSearchValue) {
          inputRef.current.value = currentSearchValue;
          // Trigger the input change to update our state
          const syntheticEvent = {
            target: { value: currentSearchValue }
          };
          if (onInputChange) {
            onInputChange(syntheticEvent, currentSearchValue, 'input');
          } else {
            setInternalInputValue(currentSearchValue);
          }
        }
      }, 0);
    }
  };

  const handleChangeTextInAutocomplete = (event) => {
    const { value = '' } = event?.target || {};
    handleChange(event);
    validateField(value);
  };

  const handleBlurTextInAutocomplete = (event) => {
    handleBlur(event);
    setFieldStateError(false);
  };

  const handleFocusTextInAutocomplete = (event) => {
    setFieldStateError(false);
    focusStart(event);
  };

  const handleInputChange = (event, newInputValue, reason) => {
    // Always keep the internal input value in sync so the visible textbox
    // reflects what the user is typing. The external onInputChange handler
    // is for notification (e.g. debounced backend search) only — if the
    // caller wants to fully control the displayed input, they pass the
    // controlled `inputValue` prop, which takes precedence at render time.
    setInternalInputValue(newInputValue);
    if (onInputChange) {
      onInputChange(event, newInputValue, reason);
    }
  };

  const renderOptionFunc = (renderProps, option, { selected }) => {
    // Extract the key from renderProps to avoid the React warning
    const { key, ...restProps } = renderProps;

    return (
      <li key={option.value || option.label || option} {...restProps}>
        <Checkbox
          className={classes.checkbox}
          icon={icon}
          checkedIcon={checkedIcon}
          checked={selected}
          color="secondary"
        />
        {option.label}
      </li>
    );
  };

  const getOptionLabel = (option) => {
    if (option) {
      if (option.label) {
        return option.label;
      } if (option.length !== 0) {
        return option;
      }
      return '';
    }
    return option?.label || option;
  };

  const filterOptionsOrNot = filterOptions ? {
    filterOptions,
  } : {};

  // Determine the input value to use
  const currentInputValue = inputValue !== undefined ? inputValue : internalInputValue;

  return (
    <Autocomplete
      multiple={multiple}
      disabled={disabled}
      options={optionList}
      {...filterOptionsOrNot}
      disableCloseOnSelect={disableCloseOnSelect}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption || renderOptionFunc}
      renderInput={(params) => (
        <TextFieldFormik
          touchedValue={touchedValue || errorValue || fieldStateError}
          errorValue={errorValue || fieldStateError}
          handleChange={handleChangeTextInAutocomplete}
          handleBlur={handleBlurTextInAutocomplete}
          onFocus={handleFocusTextInAutocomplete}
          label={label}
          showInfo={showInfo}
          disabledBox={disabledBox}
          isShowInfoV2={isShowInfoV2}
          required={required}
          addHighlights={addHighlights}
          {...removeNameFromRenderInput ? {} : { name }}
          {...params}
          inputRef={inputRef} // Add ref to access the input element
        />
      )}
      value={values}
      onChange={handleChangeAutocomplete}
      required={required}
      inputValue={currentInputValue}
      onInputChange={handleInputChange}
      {...props}
    />
  );
}

AutocompleteFieldFormik.propTypes = {
  classes: PropTypes.object,
  setFieldValue: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  values: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.string,
    PropTypes.object,
  ]),
  filterOptions: PropTypes.func,
  disabled: PropTypes.bool,
  touchedValue: PropTypes.any,
  errorValue: PropTypes.any,
  label: PropTypes.string,
  showInfo: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.bool,
  ]),
  addHighlights: PropTypes.oneOf([
    1,
    2,
  ]),
  removeNameFromRenderInput: PropTypes.bool,
  isShowInfoV2: PropTypes.bool,
  disabledBox: PropTypes.bool,
  inlineTextValidation: PropTypes.any,
  handleFocus: PropTypes.func,
  multiple: PropTypes.bool,
  disableCloseOnSelect: PropTypes.bool,
  renderOption: PropTypes.func,
  required: PropTypes.bool,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
  maintainInputOnSelection: PropTypes.bool,
};

export default withStyles(AutocompleteFieldFormik, autocompleteFieldFormikStyle);
